import { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { LeadCard, LeadCardSkeleton, EmptyState } from "@/components/telecaller/LeadCard";
import { useBulkSelect, BulkSelectToggle, BulkStatusBar } from "@/components/telecaller/BulkSelect";
import { Search, SlidersHorizontal, Contact, Sparkles, Flame, CalendarClock, X } from "lucide-react";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose,
} from "@/components/ui/drawer";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

export default function Home() {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("new"); // new | all
  const [filterOpen, setFilterOpen] = useState(false);
  const [fStatus, setFStatus] = useState("all");
  const [fTemp, setFTemp] = useState("all");
  const debRef = useRef(null);
  const bulk = useBulkSelect();

  const loadMeta = useCallback(() => {
    api.get("/analytics/my").then((r) => setStats(r.data)).catch(() => {});
    api.get("/lead-statuses").then((r) => setStatuses(r.data.items)).catch(() => {});
  }, []);

  const load = useCallback(async (pg) => {
    const params = { page: pg, page_size: 10, sort_by: "created_at", sort_dir: -1 };
    if (q) params.q = q;
    if (seg === "new") params.status = "new";
    if (fStatus !== "all") params.status = fStatus;
    if (fTemp !== "all") params.temperature = fTemp;
    const { data } = await api.get("/leads", { params });
    setTotalPages(data.total_pages);
    setItems((prev) => (pg === 1 ? data.items : [...(prev || []), ...data.items]));
  }, [q, seg, fStatus, fTemp]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => {
    setItems(null); setPage(1);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => load(1), 250);
    return () => clearTimeout(debRef.current);
  }, [load]);

  const loadMore = () => { const n = page + 1; setPage(n); load(n); };
  const activeFilters = (fStatus !== "all" ? 1 : 0) + (fTemp !== "all" ? 1 : 0);

  return (
    <div className="space-y-5 pb-28">
      <header>
        <h1 className="font-heading text-2xl font-extrabold">{greeting()}, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          {stats ? `${stats.new_leads} नए leads • ${stats.followups_due} follow-ups due` : "Loading your queue…"}
        </p>
      </header>

      {/* summary chips */}
      <div className="grid grid-cols-3 gap-2.5">
        {[
          { label: "New", value: stats?.new_leads, icon: Sparkles, cls: "text-emerald-400" },
          { label: "Hot", value: stats?.hot_leads, icon: Flame, cls: "text-red-400" },
          { label: "Due", value: stats?.followups_due, icon: CalendarClock, cls: "text-amber-400" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <s.icon className={`h-4 w-4 ${s.cls}`} />
            <div className="font-mono text-2xl font-extrabold mt-1">{s.value ?? "–"}</div>
            <div className="text-[11px] text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* search + filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="नाम, mobile या email खोजें…"
            data-testid="lead-search-input"
            className="w-full rounded-xl bg-slate-900/60 border border-slate-700 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60" />
        </div>
        <button onClick={() => setFilterOpen(true)} data-testid="open-filters"
          className="relative rounded-xl border border-slate-700 bg-slate-900/60 px-3.5 flex items-center">
          <SlidersHorizontal className="h-4 w-4 text-slate-300" />
          {activeFilters > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center">{activeFilters}</span>}
        </button>
      </div>

      {/* segments */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          {[["new", "New"], ["all", "All Leads"]].map(([v, l]) => (
            <button key={v} onClick={() => setSeg(v)} data-testid={`segment-${v}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
                seg === v ? "border-primary bg-primary/15 text-primary" : "border-slate-700 text-slate-400"
              }`}>{l}</button>
          ))}
        </div>
        {items && items.length > 0 && (
          <BulkSelectToggle selectMode={bulk.selectMode} onEnter={() => bulk.setSelectMode(true)} onExit={bulk.exit} />
        )}
      </div>

      {/* list */}
      <div className="space-y-3">
        {items === null && [0, 1, 2].map((i) => <LeadCardSkeleton key={i} />)}
        {items !== null && items.length === 0 && (
          <EmptyState icon={Contact} title="You're all caught up." subtitle="No leads match right now." />
        )}
        {items?.map((l) => <LeadCard key={l.id} lead={l} statuses={statuses} onChanged={loadMeta}
          selectable={bulk.selectMode} selected={bulk.selected.has(l.id)} onToggleSelect={bulk.toggleSel} />)}
        {items !== null && page < totalPages && (
          <button onClick={loadMore} data-testid="load-more" className="w-full rounded-xl border border-slate-700 py-3 text-sm text-slate-300">
            और leads देखें
          </button>
        )}
      </div>

      {/* filter bottom sheet */}
      <Drawer open={filterOpen} onOpenChange={setFilterOpen}>
        <DrawerContent className="bg-slate-950 border-slate-800">
          <DrawerHeader className="flex items-center justify-between">
            <DrawerTitle>Filters</DrawerTitle>
            <DrawerClose data-testid="close-filters"><X className="h-5 w-5 text-slate-400" /></DrawerClose>
          </DrawerHeader>
          <div className="px-4 pb-8 space-y-5">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Temperature</p>
              <div className="flex flex-wrap gap-2">
                {["all", "HOT", "WARM", "COLD", "UNCLASSIFIED"].map((t) => (
                  <button key={t} onClick={() => setFTemp(t)} data-testid={`filter-temp-${t}`}
                    className={`rounded-full px-3.5 py-1.5 text-sm border ${fTemp === t ? "border-primary bg-primary/15 text-primary" : "border-slate-700 text-slate-400"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Status</p>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-auto">
                <button onClick={() => setFStatus("all")} className={`rounded-full px-3.5 py-1.5 text-sm border ${fStatus === "all" ? "border-primary bg-primary/15 text-primary" : "border-slate-700 text-slate-400"}`}>All</button>
                {statuses.map((s) => (
                  <button key={s.slug} onClick={() => setFStatus(s.slug)} data-testid={`filter-status-${s.slug}`}
                    className={`rounded-full px-3.5 py-1.5 text-sm border ${fStatus === s.slug ? "border-primary bg-primary/15 text-primary" : "border-slate-700 text-slate-400"}`}>{s.name}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setFStatus("all"); setFTemp("all"); }} className="flex-1 rounded-xl border border-slate-700 py-2.5 text-sm">Clear</button>
              <button onClick={() => setFilterOpen(false)} data-testid="apply-filters" className="flex-1 rounded-xl bg-primary text-primary-foreground font-semibold py-2.5 text-sm">Apply</button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {bulk.selectMode && (
        <BulkStatusBar count={bulk.selected.size} statuses={statuses}
          onDone={{ ids: () => [...bulk.selected], done: () => { bulk.exit(); setItems(null); setPage(1); load(1); loadMeta(); } }} />
      )}
    </div>
  );
}
